'use client';

import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '@/styles/news-article.css';
import CreditedImage from '@/components/website/CreditedImage';

const fillHeightSx = {
  flex: '1 1 0',
  minHeight: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  my: 0,
};

const defaultSx = {
  my: { xs: 3, md: 4 },
};

const imageShape = PropTypes.shape({
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  photographerCredit: PropTypes.string,
});

export default function NewsArticleImageCarousel({
  images,
  title,
  fillHeight = false,
  imageOpacity = 1,
}) {
  if (!images?.length) {
    return null;
  }

  const sharedFigureSx = {
    m: 0,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    ...(fillHeight ? fillHeightSx : defaultSx),
  };

  const sharedImgStyle = fillHeight
    ? {
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'cover',
        opacity: imageOpacity,
      }
    : {
        width: 'auto',
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        opacity: imageOpacity,
      };

  const creditedWrapperSx = fillHeight
    ? { width: '100%', height: '100%', minHeight: 0 }
    : { width: 'fit-content', maxWidth: '100%', height: 'auto' };

  if (images.length === 1) {
    const image = images[0];
    return (
      <Box
        component="figure"
        className={fillHeight ? 'news-article-carousel news-article-carousel--fill' : undefined}
        sx={sharedFigureSx}
      >
        <CreditedImage
          src={image.src}
          alt={image.alt || title}
          photographerCredit={image.photographerCredit}
          imgStyle={sharedImgStyle}
          sx={creditedWrapperSx}
        />
      </Box>
    );
  }

  return (
    <Box
      className={`news-article-carousel${fillHeight ? ' news-article-carousel--fill' : ''}`}
      sx={{
        ...sharedFigureSx,
        position: 'relative',
        boxSizing: 'border-box',
        px: fillHeight ? { xs: 0, sm: 1 } : { xs: 4, sm: 5 },
        '& .swiper-button-prev, & .swiper-button-next': {
          color: 'common.black',
        },
        '& .swiper-pagination-bullet-active': {
          bgcolor: 'common.black',
        },
      }}
    >
      <Swiper
        modules={[Navigation, Pagination, A11y]}
        navigation
        pagination={{ clickable: true }}
        spaceBetween={0}
        slidesPerView={1}
        observer
        observeParents
        resizeObserver
        style={{
          width: '100%',
          maxWidth: '100%',
          margin: 0,
          ...(fillHeight ? { flex: 1, height: '100%', minHeight: 0 } : {}),
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={`${image.src}-${index}`}>
            <CreditedImage
              src={image.src}
              alt={image.alt || `${title} — image ${index + 1}`}
              photographerCredit={image.photographerCredit}
              imgStyle={sharedImgStyle}
              sx={creditedWrapperSx}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

NewsArticleImageCarousel.propTypes = {
  images: PropTypes.arrayOf(imageShape).isRequired,
  title: PropTypes.string.isRequired,
  fillHeight: PropTypes.bool,
  imageOpacity: PropTypes.number,
};
